import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminService, levelService, channelService, menuService, settingsService, uploadService, pollService } from '../api/services';
import Pagination from '../components/Pagination';
import AdminModal from '../components/AdminModal';
import AdminBountyPanel from '../components/AdminBountyPanel';
import AdminBadgePanel from '../components/AdminBadgePanel';
import AdminRewardPanel from '../components/AdminRewardPanel';
import AdminPostOrderPanel from '../components/AdminPostOrderPanel';
import UserPermissionsModal from '../components/UserPermissionsModal';
import { useSettings } from '../context/SettingsContext';
import { menuActionLabel, roleLabel, statusLabel, tagTypeLabel } from '../utils/adminLabels';
import './AdminPage.css';

const normalizeR2BrowseResponse = (res, requestedDir = '') => {
  const payload = res?.data || {};
  if (process.env.NODE_ENV !== 'production') console.log('[AdminPage] R2 browse raw data', payload);
  const rawItems = Array.isArray(payload.items) ? payload.items : Array.isArray(payload) ? payload : [];
  const currentDir = typeof payload.currentDir === 'string' ? payload.currentDir : requestedDir;

  return {
    currentDir,
    items: rawItems.map((item, index) => {
      const isDirectory = item?.isDirectory === true || item?.type === 'folder';
      const fallbackPath = item?.key || item?.path || item?.file_path || '';
      const relativePath = item?.relativePath || fallbackPath || `${currentDir}${item?.name || `item-${index}`}${isDirectory ? '/' : ''}`;
      const name = item?.name || relativePath.split('/').filter(Boolean).pop() || `未命名項目 ${index + 1}`;
      return { ...item, name, isDirectory, relativePath };
    }),
  };
};

const AdminPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [levelConfigs, setLevelConfigs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [editingJoined, setEditingJoined] = useState({});
  const [editingLevel, setEditingLevel] = useState(null);
  const [levelForm, setLevelForm] = useState({});
  const [levelCreateForm, setLevelCreateForm] = useState({ level: '', name: '', color: '#4A90D9', tag_label: '', tag_color: '#FFFFFF', tag_bg_color: '#4A90D9' });
  const [tagForm, setTagForm] = useState({ name: '', description: '', type: 'SYSTEM', status: 'ACTIVE' });
  const [editingTag, setEditingTag] = useState(null);
  const [channelForm, setChannelForm] = useState({ name: '', slug: '', description: '', icon: '💬', sort_order: 0, post_sort_order: 'newest', require_tags: false, allow_poll: false, status: 'ACTIVE' });
  const [editingChannel, setEditingChannel] = useState(null);
  const [menuForm, setMenuForm] = useState({ title: '', icon: '📎', action_type: 'LINK', action_value: '', open_new_tab: 1, sort_order: 0, parent_id: '' });
  const [editingMenu, setEditingMenu] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permUser, setPermUser] = useState(null);
  const [userForm, setUserForm] = useState({ account: '', name: '', email: '', password: '', role: 'USER', status: 'ACTIVE', nickname: '', avatar_url: '', contact_info: '', line_id: '', joined_at: '' });
  const [settings, setSettings] = useState([]);
  const [settingsForm, setSettingsForm] = useState({});
  const [r2Items, setR2Items] = useState([]);
  const [r2Dir, setR2Dir] = useState('');
  const [r2Loading, setR2Loading] = useState(false);
  const [r2Error, setR2Error] = useState('');
  const [r2RootMode, setR2RootMode] = useState('tech-forum');
  const [r2MigrationLimit, setR2MigrationLimit] = useState(20);
  const [r2MigrationLoading, setR2MigrationLoading] = useState(false);
  const [r2MigrationResult, setR2MigrationResult] = useState(null);
  const [sqlInput, setSqlInput] = useState('SELECT id, account, name FROM users LIMIT 20');
  const [sqlResult, setSqlResult] = useState(null);
  const [marquees, setMarquees] = useState([]);
  const [marqueeForm, setMarqueeForm] = useState({ content: '', start_date: '', end_date: '', color: '#007bff', font_style: 'normal', link_url: '', popup_content: '', scope: 'GLOBAL', channel_id: '', status: 'ACTIVE', poll_id: '', poll_question: '', poll_type: 'SINGLE', poll_options: '' });
  const [editingMarquee, setEditingMarquee] = useState(null);
  const [showMarqueeModal, setShowMarqueeModal] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState({});
  const { updateSetting } = useSettings();
  const itemsPerPage = 10;
  const r2RootDir = r2RootMode === 'legacy' ? '' : 'tech-forum/';
  const r2RootLabel = r2RootMode === 'legacy' ? '舊版根目錄' : 'tech-forum 根目錄';

  const applyUsersResponse = useCallback((res) => {
    setUsers(res.data?.items || res.data || []);
    setUserPagination(res.data?.pagination || { total: (res.data || []).length, page: userPage, limit: itemsPerPage, pages: 1 });
  }, [userPage, itemsPerPage]);

  const loadR2Directory = useCallback(async (dir = '', rootMode = r2RootMode) => {
    const normalizedDir = dir || '';
    setR2Loading(true);
    setR2Error('');
    try {
      const res = await uploadService.browseAdmin(normalizedDir, rootMode);
      const browse = normalizeR2BrowseResponse(res, normalizedDir);
      setR2Dir(browse.currentDir);
      setR2Items(browse.items);
    } catch (err) {
      setR2Items([]);
      setR2Error(err.message || 'R2 目錄載入失敗');
    } finally {
      setR2Loading(false);
    }
  }, [r2RootMode]);

  const resetChannelForm = () => setChannelForm({ name: '', slug: '', description: '', icon: '💬', sort_order: 0, post_sort_order: 'newest', require_tags: false, allow_poll: false, status: 'ACTIVE' });
  const resetTagForm = () => setTagForm({ name: '', description: '', type: 'SYSTEM', status: 'ACTIVE' });
  const resetMenuForm = () => setMenuForm({ title: '', icon: '📎', action_type: 'LINK', action_value: '', open_new_tab: 1, sort_order: 0, parent_id: '' });
  const resetUserForm = () => setUserForm({ account: '', name: '', email: '', password: '', role: 'USER', status: 'ACTIVE', nickname: '', avatar_url: '', contact_info: '', line_id: '', joined_at: '' });
  const resetMarqueeForm = () => setMarqueeForm({ content: '', start_date: '', end_date: '', color: '#007bff', font_style: 'normal', link_url: '', popup_content: '', scope: 'GLOBAL', channel_id: '', status: 'ACTIVE', poll_id: '', poll_question: '', poll_type: 'SINGLE', poll_options: '' });
  const requiredClass = (value, scope) => `form-control form-control-sm required-placeholder ${submitAttempted[scope] && !String(value ?? '').trim() ? 'required-missing' : ''}`;
  const resetSubmitAttempt = (scope) => setSubmitAttempted(prev => ({ ...prev, [scope]: false }));

  const openUserEditor = (selectedUser) => {
    setEditingUser(selectedUser.id);
    setShowAddUser(false);
    resetSubmitAttempt('user');
    setUserForm({
      account: selectedUser.account, name: selectedUser.name, email: selectedUser.email, password: '',
      role: selectedUser.role || 'USER', nickname: selectedUser.nickname || '', avatar_url: selectedUser.avatar_url || '',
      contact_info: selectedUser.contact_info || '', line_id: selectedUser.line_id || '', status: selectedUser.status || 'ACTIVE',
      joined_at: selectedUser.joined_at ? selectedUser.joined_at.split('T')[0] : ''
    });
  };

  const closeUserEditor = () => {
    setEditingUser(null);
    resetUserForm();
    if (new URLSearchParams(location.search).get('editUser')) navigate('/admin?tab=users', { replace: true });
  };

  useEffect(() => {
    const queryTab = new URLSearchParams(location.search).get('tab');
    if (['users', 'tags', 'levels', 'channels', 'postOrder', 'menu', 'settings', 'r2', 'sql', 'marquees', 'bounty', 'badges', 'rewards'].includes(queryTab)) setActiveTab(queryTab);
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (activeTab === 'users') {
          const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
          applyUsersResponse(res);
        } else if (activeTab === 'tags') {
          const res = await adminService.tags.list();
          setTags(res.data);
        } else if (activeTab === 'levels') {
          const res = await levelService.getConfigs();
          setLevelConfigs(res.data);
        } else if (activeTab === 'channels') {
          // 後台頻道管理需顯示全部頻道（含停用），停用的才有機會被重新啟用
          const res = await channelService.listAll();
          setChannels(res.data || []);
        } else if (activeTab === 'menu') {
          const res = await menuService.listAll();
          setMenuItems(res.data);
        } else if (activeTab === 'settings') {
          const res = await settingsService.getAllAdmin();
          setSettings(res.data);
	          setSettingsForm(Object.fromEntries((res.data || []).map(s => [s.setting_key, s.setting_value || ''])));
        } else if (activeTab === 'r2') {
          await loadR2Directory(r2RootDir);
        } else if (activeTab === 'marquees') {
          const [res, chRes] = await Promise.all([adminService.marquees.list(), channelService.listAll()]);
          setMarquees(res.data || []);
          setChannels(chRes.data || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, userPage, r2RootDir, applyUsersResponse, loadR2Directory]);

  useEffect(() => {
    const editUserId = new URLSearchParams(location.search).get('editUser');
    if (activeTab !== 'users' || !editUserId || editingUser === editUserId) return;

    const applyDeepLinkUser = (targetUser) => {
      setEditingUser(targetUser.id);
      setShowAddUser(false);
      setSubmitAttempted(prev => ({ ...prev, user: false }));
      setUserForm({
        account: targetUser.account, name: targetUser.name, email: targetUser.email, password: '',
        role: targetUser.role || 'USER', nickname: targetUser.nickname || '', avatar_url: targetUser.avatar_url || '',
        contact_info: targetUser.contact_info || '', line_id: targetUser.line_id || '', status: targetUser.status || 'ACTIVE',
        joined_at: targetUser.joined_at ? targetUser.joined_at.split('T')[0] : ''
      });
    };

    const existingUser = users.find((u) => u.id === editUserId);
    if (existingUser) {
      applyDeepLinkUser(existingUser);
      return;
    }

    let cancelled = false;
    const fetchTargetUser = async () => {
      try {
        const res = await adminService.users.get(editUserId);
        if (cancelled) return;
        // 業務邏輯：從人員名錄 deep link 進來時，若目標使用者不在目前分頁，先暫時插入列表讓管理員可直接編輯。
        setUsers((prev) => prev.some((u) => u.id === res.data.id) ? prev : [res.data, ...prev]);
        applyDeepLinkUser(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    fetchTargetUser();
    return () => { cancelled = true; };
  }, [activeTab, location.search, users, editingUser]);

  const handleUserStatusChange = async (userId, newStatus) => {
    try {
      await adminService.users.updateStatus(userId, newStatus);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTagStatusChange = async (tagId, newStatus) => {
    try {
      await adminService.tags.updateStatus(tagId, newStatus);
      const res = await adminService.tags.list();
      setTags(res.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateTag = async () => {
    setSubmitAttempted(prev => ({ ...prev, tag: true }));
    if (!tagForm.name.trim()) { setError('請填寫標籤名稱'); return; }
    try {
      await adminService.tags.create(tagForm);
      const res = await adminService.tags.list();
      setTags(res.data);
      resetTagForm();
      setShowTagModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleUpdateTag = async (id) => {
    setSubmitAttempted(prev => ({ ...prev, tag: true }));
    if (!tagForm.name.trim()) { setError('請填寫標籤名稱'); return; }
    try {
      await adminService.tags.update(id, tagForm);
      const res = await adminService.tags.list();
      setTags(res.data);
      setEditingTag(null);
      resetTagForm();
      setShowTagModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm('確定刪除此標籤？相關文章會移除此標籤關聯')) return;
    try {
      await adminService.tags.delete(id);
      const res = await adminService.tags.list();
      setTags(res.data);
    } catch (err) { setError(err.message); }
  };

  const handleSetJoinedAt = async (userId) => {
    const date = editingJoined[userId];
    if (!date) return;
    try {
      await levelService.setJoinedAt(userId, date);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
      setEditingJoined(prev => ({ ...prev, [userId]: undefined }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLevelConfigSave = async (level) => {
    try {
      await levelService.updateConfig(level, levelForm);
      const res = await levelService.getConfigs();
      setLevelConfigs(res.data);
      setEditingLevel(null);
      setLevelForm({});
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateLevelConfig = async () => {
    setSubmitAttempted(prev => ({ ...prev, level: true }));
    if (!String(levelCreateForm.level).trim() || !levelCreateForm.name.trim()) { setError('請填寫等級與名稱'); return; }
    try {
      await levelService.createConfig(levelCreateForm);
      const res = await levelService.getConfigs();
      setLevelConfigs(res.data);
      setLevelCreateForm({ level: '', name: '', color: '#4A90D9', tag_label: '', tag_color: '#FFFFFF', tag_bg_color: '#4A90D9' });
      setShowLevelModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteLevelConfig = async (level) => {
    if (!window.confirm(`確定刪除 Lv.${level} 設定？`)) return;
    try {
      await levelService.deleteConfig(level);
      const res = await levelService.getConfigs();
      setLevelConfigs(res.data);
    } catch (err) { setError(err.message); }
  };

  const handleResetUserPassword = async (id) => {
    if (!window.confirm('確定將此使用者密碼重設為預設密碼 A000000？使用者下次登入會被要求修改密碼。')) return;
    try {
      await adminService.users.resetPassword(id);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const handleCreateUser = async () => {
    setSubmitAttempted(prev => ({ ...prev, user: true }));
    if (!userForm.account.trim() || !userForm.name.trim() || !userForm.email.trim()) { setError('請填寫帳號、名稱與信箱'); return; }
    try {
      await adminService.users.create(userForm);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
      setShowAddUser(false);
      resetUserForm();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const handleUpdateUser = async (id) => {
    setSubmitAttempted(prev => ({ ...prev, user: true }));
    if (!userForm.account.trim() || !userForm.name.trim() || !userForm.email.trim()) { setError('請填寫帳號、名稱與信箱'); return; }
    try {
      const { password, ...data } = userForm;
      await adminService.users.update(id, data);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
      closeUserEditor();
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('確定停用此用戶？')) return;
    try {
      await adminService.users.delete(id);
      const res = await adminService.users.list({ page: userPage, limit: itemsPerPage });
      applyUsersResponse(res);
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  // Settings handler
  const handleUpdateSetting = async (key, value) => {
    try {
      const nextValue = String(value ?? '');
      await updateSetting(key, nextValue);
      const res = await settingsService.getAllAdmin();
      setSettings(res.data);
      setSettingsForm(Object.fromEntries((res.data || []).map(s => [s.setting_key, s.setting_value || ''])));
    } catch (err) { setError(err.response?.data?.error || err.message); }
  };

  const getSettingDescription = (setting) => {
    const descriptions = {
      site_title: '瀏覽器頁籤名稱',
      header_title: '導覽列顯示名稱',
      home_title: '首頁主標題',
      home_subtitle: '首頁副標題',
      site_name: '舊版相容設定，建議改用 site_title / header_title',
      bounty_points_enabled: '懸賞點數機制（true 開啟後，開立懸賞需填點數；亦可於「懸賞與權限」分頁切換）',
      bounty_return_enabled: '懸賞退回開關（true 允許領取後退回；亦可於「懸賞與權限」分頁切換）',
      badge_collection_enabled: '成就徽章收集功能（true 開啟，false 關閉後個人頁不顯示徽章）',
    };
    return descriptions[setting.setting_key] || setting.description;
  };

  // Channel CRUD
  const handleCreateChannel = async () => {
    setSubmitAttempted(prev => ({ ...prev, channel: true }));
    if (!channelForm.name.trim() || !channelForm.slug.trim()) { setError('請填寫頻道名稱與代號'); return; }
    try {
      await channelService.create({ ...channelForm, required_fields: channelForm.require_tags ? ['tags'] : [] });
      const res = await channelService.listAll();
      setChannels(res.data);
      resetChannelForm();
      setShowChannelModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleUpdateChannel = async (id) => {
    setSubmitAttempted(prev => ({ ...prev, channel: true }));
    if (!channelForm.name.trim() || !channelForm.slug.trim()) { setError('請填寫頻道名稱與代號'); return; }
    try {
      await channelService.update(id, { ...channelForm, required_fields: channelForm.require_tags ? ['tags'] : [] });
      const res = await channelService.listAll();
      setChannels(res.data);
      setEditingChannel(null);
      resetChannelForm();
      setShowChannelModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('確定永久刪除此頻道？頻道內的文章將移至綜合討論，此操作無法復原')) return;
    try {
      await channelService.delete(id);
      const res = await channelService.listAll();
      setChannels(res.data);
    } catch (err) { setError(err.message); }
  };

  // 快速切換頻道啟用/停用狀態：沿用完整更新 API，欄位值取自列表既有資料
  const handleChannelStatusChange = async (ch) => {
    try {
      await channelService.update(ch.id, {
        name: ch.name,
        slug: ch.slug,
        description: ch.description,
        icon: ch.icon,
        sort_order: ch.sort_order,
        required_fields: (ch.required_fields || '').includes('tags') ? ['tags'] : [],
        allow_poll: (ch.features || '').includes('poll'),
        status: ch.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
      });
      const res = await channelService.listAll();
      setChannels(res.data);
    } catch (err) { setError(err.message); }
  };

  // Menu CRUD
  const handleCreateMenu = async () => {
    setSubmitAttempted(prev => ({ ...prev, menu: true }));
    if (!menuForm.title.trim()) { setError('請填寫功能表標題'); return; }
    try {
      const data = { ...menuForm, parent_id: menuForm.parent_id || null };
      await menuService.create(data);
      const res = await menuService.listAll();
      setMenuItems(res.data);
      resetMenuForm();
      setShowMenuModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleUpdateMenu = async (id) => {
    setSubmitAttempted(prev => ({ ...prev, menu: true }));
    if (!menuForm.title.trim()) { setError('請填寫功能表標題'); return; }
    try {
      const data = { ...menuForm, parent_id: menuForm.parent_id || null };
      await menuService.update(id, data);
      const res = await menuService.listAll();
      setMenuItems(res.data);
      setEditingMenu(null);
      resetMenuForm();
      setShowMenuModal(false);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteMenu = async (id) => {
    if (!window.confirm('確定刪除此功能表項目？子項目也會被刪除')) return;
    try {
      await menuService.delete(id);
      const res = await menuService.listAll();
      setMenuItems(res.data);
    } catch (err) { setError(err.message); }
  };

  const handleDeleteR2File = async (path) => {
    if (!window.confirm('確定刪除此 R2 檔案？若仍被文章或個人資料引用，圖片/附件將無法顯示。')) return;
    try {
      setR2Error('');
      await uploadService.deleteAdminFile(path);
      await loadR2Directory(r2Dir, r2RootMode);
    } catch (err) { setError(err.message); }
  };

  const handleR2RootModeChange = async (mode) => {
    const nextRootDir = mode === 'legacy' ? '' : 'tech-forum/';
    setR2RootMode(mode);
    setR2MigrationResult(null);
    await loadR2Directory(nextRootDir, mode);
  };

  const handleR2Parent = () => {
    if (!r2Dir || r2Dir === r2RootDir) { loadR2Directory(r2RootDir, r2RootMode); return; }
    const relativeDir = r2Dir.startsWith(r2RootDir) ? r2Dir.slice(r2RootDir.length) : r2Dir;
    const parts = String(relativeDir || '').split('/').filter(Boolean);
    parts.pop();
    loadR2Directory(parts.length ? `${r2RootDir}${parts.join('/')}/` : r2RootDir, r2RootMode);
  };

  const r2RelativeDir = String(r2Dir || '').startsWith(r2RootDir) ? String(r2Dir || '').slice(r2RootDir.length) : String(r2Dir || '');
  const r2Breadcrumbs = r2RelativeDir.split('/').filter(Boolean).map((part, index, parts) => ({
    label: part,
    path: `${r2RootDir}${parts.slice(0, index + 1).join('/')}/`,
  }));

  const r2EmptyMessage = () => {
    if (r2RootMode === 'legacy' && !r2Dir) return '舊版根目錄目前沒有檔案。若正式環境有舊附件，請先部署後使用下方 dry-run 檢查 D1 attachments 與 R2 物件。';
    if (r2Dir === r2RootDir) return `找不到指定路徑：${r2RootDir || '舊版根目錄'}，或該路徑目前沒有檔案。已通過管理員權限驗證，請確認 R2 state 是否有物件。`;
    return `找不到指定路徑：${r2Dir || r2RootDir || '舊版根目錄'}，或該目錄目前沒有檔案。`;
  };

  const r2DownloadHref = (item) => {
    if (item.download_url) return uploadService.getFileUrl(item.download_url);
    return item.metadata?.id ? uploadService.getDownloadUrl(item.metadata.id) : uploadService.getFileUrl(item.relativePath || '');
  };

  const handleR2Migration = async (dryRun, deleteOld = false) => {
    if (!dryRun) {
      const message = deleteOld
        ? '確定要執行遷移並刪除舊 R2 物件？建議只在確認新路徑可正常存取後使用。'
        : '確定要執行遷移？此操作會複製 R2 物件並更新 D1 attachments，但會保留舊物件。';
      if (!window.confirm(message)) return;
    }
    try {
      setR2MigrationLoading(true);
      setR2Error('');
      const res = await uploadService.migrateAdminFiles({ dry_run: dryRun, limit: r2MigrationLimit, delete_old: deleteOld });
      setR2MigrationResult(res.data);
      if (!dryRun) await loadR2Directory(r2Dir || r2RootDir, r2RootMode);
    } catch (err) {
      setR2Error(err.message || 'R2 遷移執行失敗');
    } finally {
      setR2MigrationLoading(false);
    }
  };

  const handleRunSql = async () => {
    try {
      setError('');
      const res = await adminService.sql.execute(sqlInput);
      setSqlResult(res.data);
    } catch (err) { setError(err.message); }
  };

  const handleSaveMarquee = async () => {
    setSubmitAttempted(prev => ({ ...prev, marquee: true }));
    if (!marqueeForm.content.trim()) { setError('請填寫跑馬燈內容'); return; }
    try {
      // 綁定投票：若填了投票題目與至少兩個選項，先建立獨立投票，再以其 id 綁定到本跑馬燈；
      // 未填則沿用既有 poll_id（編輯時不至於誤清除既有綁定）。
      let pollId = marqueeForm.poll_id || null;
      const pollOpts = String(marqueeForm.poll_options || '').split('\n').map(s => s.trim()).filter(Boolean);
      if (marqueeForm.poll_question.trim() && pollOpts.length >= 2) {
        const pollRes = await pollService.create({ question: marqueeForm.poll_question.trim(), poll_type: marqueeForm.poll_type, options: pollOpts });
        pollId = pollRes.data.id;
      }
      const payload = { ...marqueeForm, channel_id: marqueeForm.scope === 'CHANNEL' ? marqueeForm.channel_id : '', poll_id: pollId };
      if (editingMarquee) await adminService.marquees.update(editingMarquee, payload);
      else await adminService.marquees.create(payload);
      const res = await adminService.marquees.list();
      setMarquees(res.data || []);
      setEditingMarquee(null);
      setShowMarqueeModal(false);
      resetMarqueeForm();
      resetSubmitAttempt('marquee');
    } catch (err) { setError(err.message); }
  };

  const handleDeleteMarquee = async (id) => {
    if (!window.confirm('確定刪除此公告？')) return;
    try {
      await adminService.marquees.delete(id);
      const res = await adminService.marquees.list();
      setMarquees(res.data || []);
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <h1>管理後台</h1>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="admin-tabs">
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            使用者管理
          </button>
          <button
            className={`tab ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            標籤管理
          </button>
          <button
            className={`tab ${activeTab === 'levels' ? 'active' : ''}`}
            onClick={() => setActiveTab('levels')}
          >
            等級管理
          </button>
          <button
            className={`tab ${activeTab === 'channels' ? 'active' : ''}`}
            onClick={() => setActiveTab('channels')}
          >
            頻道管理
          </button>
          <button
            className={`tab ${activeTab === 'postOrder' ? 'active' : ''}`}
            onClick={() => setActiveTab('postOrder')}
          >
            文章排序
          </button>
          <button
            className={`tab ${activeTab === 'menu' ? 'active' : ''}`}
            onClick={() => setActiveTab('menu')}
          >
            功能表管理
          </button>
          <button
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ 系統設定
          </button>
          <button className={`tab ${activeTab === 'r2' ? 'active' : ''}`} onClick={() => setActiveTab('r2')}>R2 檔案管理</button>
          <button className={`tab ${activeTab === 'sql' ? 'active' : ''}`} onClick={() => setActiveTab('sql')}>SQL 查詢</button>
          <button className={`tab ${activeTab === 'marquees' ? 'active' : ''}`} onClick={() => setActiveTab('marquees')}>跑馬燈公告</button>
          <button className={`tab ${activeTab === 'bounty' ? 'active' : ''}`} onClick={() => setActiveTab('bounty')}>懸賞與權限</button>
          <button className={`tab ${activeTab === 'badges' ? 'active' : ''}`} onClick={() => setActiveTab('badges')}>徽章管理</button>
          <button className={`tab ${activeTab === 'rewards' ? 'active' : ''}`} onClick={() => setActiveTab('rewards')}>點數兌換</button>
        </div>

        <div className="admin-content">
          {activeTab === 'bounty' && <AdminBountyPanel />}
          {activeTab === 'badges' && <AdminBadgePanel />}
          {activeTab === 'rewards' && <AdminRewardPanel />}
          {activeTab === 'postOrder' && <AdminPostOrderPanel />}
          {activeTab === 'users' && (
            <div className="users-section">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h2 style={{margin:0}}>使用者列表</h2>
	                <button className="btn btn-sm btn-primary" onClick={() => { setShowAddUser(!showAddUser); setEditingUser(null); resetUserForm(); resetSubmitAttempt('user'); }}>
	                  ＋ 新增用戶
                </button>
              </div>

	              {showAddUser && (
	                <AdminModal title="新增用戶" onClose={() => { setShowAddUser(false); resetUserForm(); resetSubmitAttempt('user'); }}>
	                <div className="admin-form-row" style={{flexDirection:'column',alignItems:'stretch',gap:12}}>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
	                    <input className={requiredClass(userForm.account, 'user')} placeholder="帳號 *" required value={userForm.account} onChange={e => setUserForm(p => ({...p, account: e.target.value}))} />
	                    <input className={requiredClass(userForm.name, 'user')} placeholder="名稱 *" required value={userForm.name} onChange={e => setUserForm(p => ({...p, name: e.target.value}))} />
	                    <input className={requiredClass(userForm.email, 'user')} type="email" placeholder="信箱 *" required value={userForm.email} onChange={e => setUserForm(p => ({...p, email: e.target.value}))} />
                    <select className="form-control form-control-sm" value={userForm.role} onChange={e => setUserForm(p => ({...p, role: e.target.value}))}>
	                      <option value="USER">一般使用者</option>
	                      <option value="ADMIN">管理員</option>
                      <option value="MODERATOR">版主</option>
                      <option value="SE">SE</option>
                      <option value="PERSON">person</option>
                    </select>
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    <input className="form-control form-control-sm" placeholder="暱稱" value={userForm.nickname} onChange={e => setUserForm(p => ({...p, nickname: e.target.value}))} />
	                    <input className="form-control form-control-sm" placeholder="頭像連結" value={userForm.avatar_url} onChange={e => setUserForm(p => ({...p, avatar_url: e.target.value}))} />
                    <input className="form-control form-control-sm" placeholder="聯絡方式" value={userForm.contact_info} onChange={e => setUserForm(p => ({...p, contact_info: e.target.value}))} />
	                    <input className="form-control form-control-sm" placeholder="LINE 帳號" value={userForm.line_id} onChange={e => setUserForm(p => ({...p, line_id: e.target.value}))} />
                    <input className="form-control form-control-sm" type="date" placeholder="入職日" value={userForm.joined_at} onChange={e => setUserForm(p => ({...p, joined_at: e.target.value}))} />
                  </div>
	                  <div className="admin-note">由管理員新增帳號時，系統會統一設定預設密碼 A000000，使用者首次登入後會被強制修改密碼。</div>
                  <button className="btn btn-sm btn-primary" style={{alignSelf:'flex-end'}} onClick={handleCreateUser}>建立用戶</button>
                </div>
	                </AdminModal>
              )}

              {editingUser && (
                <AdminModal title="編輯用戶" onClose={closeUserEditor}>
                  <div className="admin-form-row" style={{flexDirection:'column',alignItems:'stretch',gap:12}}>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
	                      <input className={requiredClass(userForm.account, 'user')} placeholder="帳號 *" required value={userForm.account} onChange={e => setUserForm(p => ({...p, account: e.target.value}))} />
                      <input className={requiredClass(userForm.name, 'user')} placeholder="名稱 *" required value={userForm.name} onChange={e => setUserForm(p => ({...p, name: e.target.value}))} />
                      <input className={requiredClass(userForm.email, 'user')} type="email" placeholder="信箱 *" required value={userForm.email} onChange={e => setUserForm(p => ({...p, email: e.target.value}))} />
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                      <input className="form-control form-control-sm" placeholder="暱稱" value={userForm.nickname} onChange={e => setUserForm(p => ({...p, nickname: e.target.value}))} />
                      <input className="form-control form-control-sm" placeholder="頭像連結" value={userForm.avatar_url} onChange={e => setUserForm(p => ({...p, avatar_url: e.target.value}))} />
                      <input className="form-control form-control-sm" placeholder="聯絡方式" value={userForm.contact_info} onChange={e => setUserForm(p => ({...p, contact_info: e.target.value}))} />
                      <input className="form-control form-control-sm" placeholder="LINE 帳號" value={userForm.line_id} onChange={e => setUserForm(p => ({...p, line_id: e.target.value}))} />
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                      <select className="form-control form-control-sm" value={userForm.role} onChange={e => setUserForm(p => ({...p, role: e.target.value}))}>
                        <option value="USER">一般使用者</option>
                        <option value="ADMIN">管理員</option>
                      <option value="MODERATOR">版主</option>
                      <option value="SE">SE</option>
                      <option value="PERSON">person</option>
                      </select>
                      <select className="form-control form-control-sm" value={userForm.status} onChange={e => setUserForm(p => ({...p, status: e.target.value}))}>
                        <option value="ACTIVE">啟用</option>
                        <option value="DISABLED">停用</option>
                      </select>
                      <input className="form-control form-control-sm" type="date" value={userForm.joined_at} onChange={e => setUserForm(p => ({...p, joined_at: e.target.value}))} />
                    </div>
                    <div className="btn-group" style={{justifyContent:'flex-end'}}>
                      <button className="btn btn-sm btn-primary" onClick={() => handleUpdateUser(editingUser)}>儲存</button>
                      <button className="btn btn-sm btn-secondary" onClick={closeUserEditor}>取消</button>
                    </div>
                  </div>
                </AdminModal>
              )}

              <table className="admin-table">
                <thead>
                  <tr>
                    <th>帳號</th>
                    <th>名稱</th>
                    <th>暱稱</th>
                    <th>信箱</th>
	                    <th>LINE 帳號</th>
                    <th>角色</th>
                    <th>入職日</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.account}</td>
	                      <td>{user.name}</td>
	                      <td>{user.nickname || '-'}</td>
	                      <td>{user.email}</td>
	                      <td>{user.line_id || '-'}</td>
	                      <td>{roleLabel(user.role || 'USER')}</td>
                      <td>
                        <div className="joined-at-cell">
	                          <input
	                            type="date"
	                            className="form-control form-control-sm"
	                            value={editingJoined[user.id] || (user.joined_at ? user.joined_at.split('T')[0] : '')}
	                            onChange={(e) => setEditingJoined(prev => ({ ...prev, [user.id]: e.target.value }))}
	                          />
	                          {editingJoined[user.id] && editingJoined[user.id] !== (user.joined_at ? user.joined_at.split('T')[0] : '') && (
	                            <button className="btn btn-sm btn-primary" onClick={() => handleSetJoinedAt(user.id)}>儲存</button>
	                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${user.status === 'ACTIVE' ? 'success' : 'danger'}`}>
	                          {statusLabel(user.status)}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
	                          <button className="btn btn-sm btn-secondary" onClick={() => openUserEditor(user)}>編輯</button>
		                          <button className="btn btn-sm btn-secondary" onClick={() => setPermUser(user)}>權限</button>
	                          <button className="btn btn-sm btn-secondary" onClick={() => handleUserStatusChange(user.id, user.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE')}>
	                            {user.status === 'ACTIVE' ? '停用' : '啟用'}
	                          </button>
		                      <button className="btn btn-sm btn-secondary" onClick={() => handleResetUserPassword(user.id)}>重設密碼</button>
	                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(user.id)}>刪除</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length > 0 && (
	                <Pagination pagination={userPagination} currentPage={userPage} onPageChange={setUserPage} unitLabel="位使用者" />
              )}
              {permUser && (
                <UserPermissionsModal user={permUser} onClose={() => setPermUser(null)} />
              )}
            </div>
          )}

          {activeTab === 'tags' && (
            <div className="tags-section">
	              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
		              <h2 style={{margin:0}}>標籤管理</h2>
	                <button className="btn btn-sm btn-primary" onClick={() => { setEditingTag(null); resetTagForm(); resetSubmitAttempt('tag'); setShowTagModal(true); }}>＋ 新增標籤</button>
	              </div>
	              {showTagModal && (
	                <AdminModal title={editingTag ? '編輯標籤' : '新增標籤'} onClose={() => { setShowTagModal(false); setEditingTag(null); resetTagForm(); resetSubmitAttempt('tag'); }}>
	              <div className="admin-form-row">
		                <input className={requiredClass(tagForm.name, 'tag')} placeholder="標籤名稱 *" required value={tagForm.name} onChange={e => setTagForm(p => ({...p, name: e.target.value}))} />
	                <input className="form-control form-control-sm" placeholder="說明" value={tagForm.description} onChange={e => setTagForm(p => ({...p, description: e.target.value}))} />
	                <select className="form-control form-control-sm" value={tagForm.type} onChange={e => setTagForm(p => ({...p, type: e.target.value}))}>
		                  <option value="SYSTEM">系統標籤</option>
		                  <option value="USER_DEFINED">使用者自訂</option>
	                </select>
	                <select className="form-control form-control-sm" value={tagForm.status} onChange={e => setTagForm(p => ({...p, status: e.target.value}))}>
		                  <option value="ACTIVE">啟用</option>
		                  <option value="DISABLED">停用</option>
	                </select>
	                {editingTag ? (
	                  <div className="btn-group">
	                    <button className="btn btn-sm btn-primary" onClick={() => handleUpdateTag(editingTag)}>更新</button>
	                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditingTag(null); resetTagForm(); }}>取消</button>
	                  </div>
	                ) : (
	                  <button className="btn btn-sm btn-primary" onClick={handleCreateTag}>新增</button>
	                )}
	              </div>
	                </AdminModal>
	              )}
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>名稱</th>
                    <th>類型</th>
                    <th>狀態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag.id}>
	                      <td>{tag.name}</td>
	                      <td>{tagTypeLabel(tag.type)}</td>
                      <td>
                        <span
                          className={`badge badge-${
                            tag.status === 'ACTIVE' ? 'success' : 'danger'
                          }`}
                        >
	                          {statusLabel(tag.status)}
                        </span>
                      </td>
                      <td>
	                        <div className="btn-group">
	                          <button className="btn btn-sm btn-secondary" onClick={() => { setEditingTag(tag.id); setTagForm({ name: tag.name, description: tag.description || '', type: tag.type || 'SYSTEM', status: tag.status || 'ACTIVE' }); resetSubmitAttempt('tag'); setShowTagModal(true); }}>編輯</button>
	                          <button className="btn btn-sm btn-secondary" onClick={() => handleTagStatusChange(tag.id, tag.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE')}>{tag.status === 'ACTIVE' ? '停用' : '啟用'}</button>
	                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteTag(tag.id)}>刪除</button>
	                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'levels' && (
            <div className="levels-section">
	              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
	                <h2 style={{margin:0}}>等級配置</h2>
	                <button className="btn btn-sm btn-primary" onClick={() => { resetSubmitAttempt('level'); setShowLevelModal(true); }}>＋ 新增等級</button>
	              </div>
	              {showLevelModal && (
	                <AdminModal title="新增等級" onClose={() => { setShowLevelModal(false); resetSubmitAttempt('level'); }}>
	              <div className="admin-form-row">
		                <input className={requiredClass(levelCreateForm.level, 'level')} type="number" placeholder="等級 *" required value={levelCreateForm.level} onChange={e => setLevelCreateForm(p => ({...p, level: e.target.value}))} />
		                <input className={requiredClass(levelCreateForm.name, 'level')} placeholder="名稱 *" required value={levelCreateForm.name} onChange={e => setLevelCreateForm(p => ({...p, name: e.target.value}))} />
	                <label>條色</label><input type="color" value={levelCreateForm.color} onChange={e => setLevelCreateForm(p => ({...p, color: e.target.value}))} />
	                <input className="form-control form-control-sm" placeholder="標籤文字" value={levelCreateForm.tag_label} onChange={e => setLevelCreateForm(p => ({...p, tag_label: e.target.value}))} />
	                <label>文字色</label><input type="color" value={levelCreateForm.tag_color} onChange={e => setLevelCreateForm(p => ({...p, tag_color: e.target.value}))} />
	                <label>背景色</label><input type="color" value={levelCreateForm.tag_bg_color} onChange={e => setLevelCreateForm(p => ({...p, tag_bg_color: e.target.value}))} />
	                <button className="btn btn-sm btn-primary" onClick={handleCreateLevelConfig}>新增</button>
	              </div>
	                </AdminModal>
	              )}
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>等級</th>
                    <th>名稱</th>
                    <th>顏色</th>
                    <th>標籤</th>
                    <th>預覽</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {levelConfigs.map((cfg) => (
                    <tr key={cfg.level}>
                      <td>{cfg.level}</td>
                      <td>
                        {editingLevel === cfg.level ? (
                          <input
                            className="form-control form-control-sm"
                            value={levelForm.name ?? cfg.name}
                            onChange={(e) => setLevelForm(prev => ({ ...prev, name: e.target.value }))}
                          />
                        ) : cfg.name}
                      </td>
                      <td>
                        {editingLevel === cfg.level ? (
                          <div className="color-inputs">
                            <label>條色</label>
                            <input type="color" value={levelForm.color ?? cfg.color} onChange={(e) => setLevelForm(prev => ({ ...prev, color: e.target.value }))} />
                            <label>標籤背景</label>
                            <input type="color" value={levelForm.tag_bg_color ?? cfg.tag_bg_color} onChange={(e) => setLevelForm(prev => ({ ...prev, tag_bg_color: e.target.value }))} />
                            <label>標籤文字</label>
                            <input type="color" value={levelForm.tag_color ?? cfg.tag_color} onChange={(e) => setLevelForm(prev => ({ ...prev, tag_color: e.target.value }))} />
                          </div>
                        ) : (
                          <div className="color-preview" style={{ backgroundColor: cfg.color, width: 30, height: 20, borderRadius: 4 }} />
                        )}
                      </td>
                      <td>
                        {editingLevel === cfg.level ? (
                          <input
                            className="form-control form-control-sm"
                            value={levelForm.tag_label ?? cfg.tag_label}
                            onChange={(e) => setLevelForm(prev => ({ ...prev, tag_label: e.target.value }))}
                          />
                        ) : cfg.tag_label}
                      </td>
                      <td>
                        <span
                          className="level-tag-preview"
                          style={{ color: cfg.tag_color, backgroundColor: cfg.tag_bg_color, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}
                        >
                          {cfg.tag_label || `Lv.${cfg.level}`}
                        </span>
                      </td>
                      <td>
                        {editingLevel === cfg.level ? (
                          <div className="btn-group">
                            <button className="btn btn-sm btn-primary" onClick={() => handleLevelConfigSave(cfg.level)}>儲存</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => { setEditingLevel(null); setLevelForm({}); }}>取消</button>
                          </div>
	                        ) : (
	                          <div className="btn-group">
	                            <button className="btn btn-sm btn-secondary" onClick={() => { setEditingLevel(cfg.level); setLevelForm({ name: cfg.name, color: cfg.color, tag_label: cfg.tag_label, tag_color: cfg.tag_color, tag_bg_color: cfg.tag_bg_color }); }}>編輯</button>
	                            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteLevelConfig(cfg.level)}>刪除</button>
	                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'channels' && (
            <div className="channels-section">
	              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
	                <h2 style={{margin:0}}>頻道管理</h2>
	                <button className="btn btn-sm btn-primary" onClick={() => { setEditingChannel(null); resetChannelForm(); resetSubmitAttempt('channel'); setShowChannelModal(true); }}>＋ 新增頻道</button>
	              </div>
	              {showChannelModal && (
	                <AdminModal title={editingChannel ? '編輯頻道' : '新增頻道'} onClose={() => { setShowChannelModal(false); setEditingChannel(null); resetChannelForm(); resetSubmitAttempt('channel'); }}>
              <div className="admin-form-row">
		                <input className={requiredClass(channelForm.name, 'channel')} placeholder="名稱 *" required value={channelForm.name} onChange={e => setChannelForm(p => ({ ...p, name: e.target.value }))} />
		                <input className={requiredClass(channelForm.slug, 'channel')} placeholder="代號 *" required value={channelForm.slug} onChange={e => setChannelForm(p => ({ ...p, slug: e.target.value }))} />
                <input className="form-control form-control-sm" placeholder="圖示" value={channelForm.icon} onChange={e => setChannelForm(p => ({ ...p, icon: e.target.value }))} style={{width: 60}} />
                <input className="form-control form-control-sm" placeholder="說明" value={channelForm.description} onChange={e => setChannelForm(p => ({ ...p, description: e.target.value }))} />
                <input className="form-control form-control-sm" type="number" placeholder="排序" value={channelForm.sort_order} onChange={e => setChannelForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={{width: 70}} />
                <select className="form-control form-control-sm" value={channelForm.post_sort_order} onChange={e => setChannelForm(p => ({ ...p, post_sort_order: e.target.value }))}>
                  <option value="newest">最新發表</option>
                  <option value="popular">最熱門</option>
                  <option value="answered">已解決優先</option>
                  <option value="custom">自定義順序</option>
                </select>
	                <select className="form-control form-control-sm" value={channelForm.status} onChange={e => setChannelForm(p => ({ ...p, status: e.target.value }))}>
		                  <option value="ACTIVE">啟用</option>
		                  <option value="DISABLED">停用</option>
	                </select>
	                <label className="form-check-inline"><input type="checkbox" checked={channelForm.require_tags} onChange={e => setChannelForm(p => ({ ...p, require_tags: e.target.checked }))} /> 發文必選標籤</label>
	                <label className="form-check-inline"><input type="checkbox" checked={channelForm.allow_poll} onChange={e => setChannelForm(p => ({ ...p, allow_poll: e.target.checked }))} /> 允許發投票文</label>
                {editingChannel ? (
                  <div className="btn-group">
                    <button className="btn btn-sm btn-primary" onClick={() => handleUpdateChannel(editingChannel)}>更新</button>
	                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditingChannel(null); resetChannelForm(); }}>取消</button>
                  </div>
                ) : (
                  <button className="btn btn-sm btn-primary" onClick={handleCreateChannel}>新增</button>
                )}
              </div>
	                </AdminModal>
	              )}
              <table className="admin-table">
                <thead>
		                  <tr><th>圖示</th><th>名稱</th><th>代號</th><th>說明</th><th>排序</th><th>文章排序</th><th>必選標籤</th><th>狀態</th><th>操作</th></tr>
                </thead>
                <tbody>
                  {channels.map(ch => (
                    <tr key={ch.id}>
                      <td>{ch.icon}</td>
                      <td>{ch.name}</td>
	                      <td><code>{ch.slug}</code></td>
                      <td>{ch.description}</td>
                      <td>{ch.sort_order}</td>
                      <td>{ch.post_sort_order === 'newest' ? '最新' : ch.post_sort_order === 'popular' ? '熱度' : ch.post_sort_order === 'answered' ? '已解決' : '自訂'}</td>
	                      <td>{(ch.required_fields || '').includes('tags') ? '是' : '否'}</td>
	                      <td><span className={`badge badge-${ch.status === 'ACTIVE' ? 'success' : 'danger'}`}>{statusLabel(ch.status)}</span></td>
                      <td>
                        <div className="btn-group">
	                          <button className="btn btn-sm btn-secondary" onClick={() => { setEditingChannel(ch.id); setChannelForm({ name: ch.name, slug: ch.slug, description: ch.description, icon: ch.icon, sort_order: ch.sort_order, post_sort_order: ch.post_sort_order || 'newest', status: ch.status || 'ACTIVE', require_tags: (ch.required_fields || '').includes('tags'), allow_poll: (ch.features || '').includes('poll') }); resetSubmitAttempt('channel'); setShowChannelModal(true); }}>編輯</button>
	                          <button className="btn btn-sm btn-secondary" onClick={() => handleChannelStatusChange(ch)}>{ch.status === 'ACTIVE' ? '停用' : '啟用'}</button>
                          {ch.id !== 'ch-general' && <button className="btn btn-sm btn-danger" onClick={() => handleDeleteChannel(ch.id)}>刪除</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'menu' && (
            <div className="menu-section">
	              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
	                <h2 style={{margin:0}}>功能表管理</h2>
	                <button className="btn btn-sm btn-primary" onClick={() => { setEditingMenu(null); resetMenuForm(); resetSubmitAttempt('menu'); setShowMenuModal(true); }}>＋ 新增功能表</button>
	              </div>
	              {showMenuModal && (
	                <AdminModal title={editingMenu ? '編輯功能表' : '新增功能表'} onClose={() => { setShowMenuModal(false); setEditingMenu(null); resetMenuForm(); resetSubmitAttempt('menu'); }}>
              <div className="admin-form-row">
		                <input className={requiredClass(menuForm.title, 'menu')} placeholder="標題 *" required value={menuForm.title} onChange={e => setMenuForm(p => ({ ...p, title: e.target.value }))} />
                <input className="form-control form-control-sm" placeholder="圖示" value={menuForm.icon} onChange={e => setMenuForm(p => ({ ...p, icon: e.target.value }))} style={{width: 60}} />
                <select className="form-control form-control-sm" value={menuForm.action_type} onChange={e => setMenuForm(p => ({ ...p, action_type: e.target.value }))}>
                  <option value="LINK">連結</option>
	                  <option value="IFRAME">嵌入頁面</option>
                  <option value="SUBMENU">子選單</option>
                </select>
                {menuForm.action_type !== 'SUBMENU' && (
	                  <input className="form-control form-control-sm" placeholder="連結網址" value={menuForm.action_value} onChange={e => setMenuForm(p => ({ ...p, action_value: e.target.value }))} />
                )}
                <select className="form-control form-control-sm" value={menuForm.parent_id || ''} onChange={e => setMenuForm(p => ({ ...p, parent_id: e.target.value }))}>
                  <option value="">頂層</option>
                  {menuItems.filter(m => m.action_type === 'SUBMENU').map(m => (
                    <option key={m.id} value={m.id}>{m.title}</option>
                  ))}
                </select>
                <input className="form-control form-control-sm" type="number" placeholder="排序" value={menuForm.sort_order} onChange={e => setMenuForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={{width: 70}} />
                <label className="form-check-inline"><input type="checkbox" checked={menuForm.open_new_tab} onChange={e => setMenuForm(p => ({ ...p, open_new_tab: e.target.checked ? 1 : 0 }))} /> 新分頁</label>
                {editingMenu ? (
                  <div className="btn-group">
                    <button className="btn btn-sm btn-primary" onClick={() => handleUpdateMenu(editingMenu)}>更新</button>
	                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditingMenu(null); resetMenuForm(); setShowMenuModal(false); }}>取消</button>
                  </div>
                ) : (
                  <button className="btn btn-sm btn-primary" onClick={handleCreateMenu}>新增</button>
                )}
              </div>
	                </AdminModal>
	              )}
              <table className="admin-table">
                <thead>
                  <tr><th>圖示</th><th>標題</th><th>類型</th><th>值</th><th>上層</th><th>排序</th><th>新分頁</th><th>狀態</th><th>操作</th></tr>
                </thead>
                <tbody>
                  {menuItems.map(item => (
                    <tr key={item.id}>
                      <td>{item.icon}</td>
                      <td>{item.title}</td>
	                      <td><span className={`badge badge-${item.action_type === 'LINK' ? 'success' : item.action_type === 'IFRAME' ? 'warning' : 'info'}`}>{menuActionLabel(item.action_type)}</span></td>
                      <td style={{maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis'}}>{item.action_value || '-'}</td>
                      <td>{item.parent_id ? menuItems.find(m => m.id === item.parent_id)?.title || item.parent_id : '-'}</td>
                      <td>{item.sort_order}</td>
	                      <td>{item.open_new_tab ? '是' : '否'}</td>
	                      <td><span className={`badge badge-${item.status === 'ACTIVE' ? 'success' : 'danger'}`}>{statusLabel(item.status)}</span></td>
                      <td>
                        <div className="btn-group">
	                          <button className="btn btn-sm btn-secondary" onClick={() => { setEditingMenu(item.id); setMenuForm({ title: item.title, icon: item.icon, action_type: item.action_type, action_value: item.action_value, open_new_tab: item.open_new_tab, sort_order: item.sort_order, parent_id: item.parent_id || '' }); resetSubmitAttempt('menu'); setShowMenuModal(true); }}>編輯</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteMenu(item.id)}>刪除</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'r2' && (
            <div className="r2-section">
              <h2>R2 檔案管理</h2>
              <div className="r2-root-switch">
                <button className={`btn btn-sm ${r2RootMode === 'tech-forum' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleR2RootModeChange('tech-forum')}>tech-forum 根目錄</button>
                <button className={`btn btn-sm ${r2RootMode === 'legacy' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleR2RootModeChange('legacy')}>舊版根目錄</button>
              </div>
              <div className="r2-toolbar">
                <div className="r2-breadcrumbs" aria-label="R2 path breadcrumbs">
                  <button className="r2-crumb" onClick={() => loadR2Directory(r2RootDir, r2RootMode)}>{r2RootLabel}</button>
                  {r2Breadcrumbs.map((crumb) => (
                    <React.Fragment key={crumb.path}>
                      <span className="r2-separator">/</span>
                      <button className="r2-crumb" onClick={() => loadR2Directory(crumb.path, r2RootMode)}>{crumb.label}</button>
                    </React.Fragment>
                  ))}
                </div>
                <div className="btn-group">
                  <button className="btn btn-sm btn-secondary" onClick={handleR2Parent} disabled={!r2Dir || r2Dir === r2RootDir}>回上一層</button>
                  <button className="btn btn-sm btn-primary" onClick={() => loadR2Directory(r2Dir, r2RootMode)}>重新整理</button>
                </div>
              </div>
              <div className="r2-status-line">
                管理根路徑：<code>{r2RootLabel}</code>，目前目錄：<code>{r2Dir || r2RootLabel}</code>，已載入 {r2Items.length} 個項目
              </div>
              <div className="admin-note">點擊資料夾列即可進入下一層；刪除檔案後會停留在目前目錄並自動刷新。</div>
              <div className="r2-migration-panel">
                <div>
                  <strong>舊檔案安全遷移</strong>
                  <div className="admin-note">先 Dry-run 預覽，再小批次遷移；預設保留舊 R2 物件，確認無誤後才建議刪除舊物件。</div>
                </div>
                <div className="r2-migration-actions">
                  <label>批次數量 <input className="form-control form-control-sm" type="number" min="1" max="50" value={r2MigrationLimit} onChange={e => setR2MigrationLimit(parseInt(e.target.value, 10) || 20)} /></label>
                  <button className="btn btn-sm btn-secondary" disabled={r2MigrationLoading} onClick={() => handleR2Migration(true)}>Dry-run</button>
                  <button className="btn btn-sm btn-primary" disabled={r2MigrationLoading} onClick={() => handleR2Migration(false, false)}>遷移並保留舊檔</button>
                  <button className="btn btn-sm btn-danger" disabled={r2MigrationLoading} onClick={() => handleR2Migration(false, true)}>遷移並刪除舊檔</button>
                </div>
                {r2MigrationLoading && <div className="r2-state-box">正在執行遷移檢查...</div>}
                {r2MigrationResult && (
                  <div className="r2-migration-result">
                    <div>模式：{r2MigrationResult.dry_run ? 'Dry-run' : '正式遷移'}，掃描 {r2MigrationResult.scanned} 筆，成功 {r2MigrationResult.migrated} 筆，失敗 {r2MigrationResult.failed} 筆，{r2MigrationResult.has_more ? '仍有下一批' : '目前批次已結束'}</div>
                    {(r2MigrationResult.results || []).slice(0, 10).map((item, index) => (
                      <div key={`${item.id}-${index}`} className={`r2-migration-row r2-migration-${item.status}`}>
                        {item.status}: <code>{item.old_path}</code> → <code>{item.new_path}</code>{item.error ? `，錯誤：${item.error}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {r2Error && <div className="alert alert-danger">R2 目錄載入失敗：{r2Error}</div>}
              {r2Loading ? (
                <div className="r2-state-box">正在載入 R2 目錄...</div>
              ) : (
                <table className="admin-table r2-table">
                  <thead><tr><th>名稱</th><th>路徑</th><th>大小</th><th>Metadata</th><th>狀態</th><th>操作</th></tr></thead>
                  <tbody>
                    {r2Items.map((item, index) => (
                      <tr key={item.relativePath || `${item.name}-${index}`} className={item.isDirectory ? 'r2-folder-row' : ''} onDoubleClick={() => item.isDirectory && loadR2Directory(item.relativePath, r2RootMode)}>
                        <td>
                          {item.isDirectory ? (
                            <button className="r2-folder-button" onClick={() => loadR2Directory(item.relativePath, r2RootMode)} title="進入資料夾">
                              <span className="r2-icon">📁</span>{item.name}
                            </button>
                          ) : (
                            <span><span className="r2-icon">📄</span>{item.name}</span>
                          )}
                        </td>
                        <td><code>{item.relativePath || '-'}</code></td>
                        <td>{item.size || '-'}</td>
                        <td>{item.metadata ? `${item.metadata.original_name || item.metadata.id} / ${item.metadata.mime_type || '-'}` : '-'}</td>
                        <td>{item.isDirectory ? '-' : <span className={`badge badge-${item.orphaned ? 'warning' : 'success'}`}>{item.orphaned ? '孤兒檔案' : '已登記'}</span>}</td>
                        <td>{!item.isDirectory && <div className="btn-group"><a className="btn btn-sm btn-secondary" href={r2DownloadHref(item)} target="_blank" rel="noreferrer">下載</a><button className="btn btn-sm btn-danger" onClick={() => handleDeleteR2File(item.relativePath)}>刪除</button></div>}</td>
                      </tr>
                    ))}
                    {r2Items.length === 0 && <tr><td colSpan="6"><div className="r2-state-box">{r2EmptyMessage()}</div></td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'sql' && (
            <div className="sql-section">
              <h2>SQL 查詢 Console</h2>
              <div className="admin-note">資安限制：僅允許 SELECT / WITH 與安全 PRAGMA 查詢，不允許寫入或破壞性 SQL。</div>
              <textarea className="form-control" rows="5" value={sqlInput} onChange={e => setSqlInput(e.target.value)} />
              <button className="btn btn-sm btn-primary" style={{marginTop: 12}} onClick={handleRunSql}>執行查詢</button>
              {sqlResult && (
                <table className="admin-table" style={{marginTop: 16}}>
                  <thead><tr>{(sqlResult.columns || []).map(col => <th key={col}>{col}</th>)}</tr></thead>
                  <tbody>
                    {(sqlResult.rows || []).map((row, idx) => <tr key={idx}>{(sqlResult.columns || []).map(col => <td key={col}>{String(row[col] ?? '')}</td>)}</tr>)}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'marquees' && (
            <div className="marquee-section">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <h2 style={{margin:0}}>跑馬燈公告</h2>
                <button className="btn btn-sm btn-primary" onClick={() => { setEditingMarquee(null); resetMarqueeForm(); resetSubmitAttempt('marquee'); setShowMarqueeModal(true); }}>＋ 新增公告</button>
              </div>
              {showMarqueeModal && (
                <AdminModal title={editingMarquee ? '編輯公告' : '新增公告'} onClose={() => { setShowMarqueeModal(false); setEditingMarquee(null); resetMarqueeForm(); }}>
                  <div className="admin-form-row" style={{flexDirection:'column',alignItems:'stretch'}}>
                    <input className={requiredClass(marqueeForm.content, 'marquee')} placeholder="公告內容 *" value={marqueeForm.content} onChange={e => setMarqueeForm(p => ({...p, content: e.target.value}))} />
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      <input className="form-control form-control-sm" type="date" value={marqueeForm.start_date} onChange={e => setMarqueeForm(p => ({...p, start_date: e.target.value}))} />
                      <input className="form-control form-control-sm" type="date" value={marqueeForm.end_date} onChange={e => setMarqueeForm(p => ({...p, end_date: e.target.value}))} />
                      <input className="form-control form-control-sm" type="color" value={marqueeForm.color} onChange={e => setMarqueeForm(p => ({...p, color: e.target.value}))} />
                      <select className="form-control form-control-sm" value={marqueeForm.font_style} onChange={e => setMarqueeForm(p => ({...p, font_style: e.target.value}))}><option value="normal">一般</option><option value="italic">斜體</option></select>
                    </div>
                    <input className="form-control form-control-sm" placeholder="連結 URL" value={marqueeForm.link_url} onChange={e => setMarqueeForm(p => ({...p, link_url: e.target.value}))} />
                    <textarea className="form-control form-control-sm" rows="4" placeholder="Popup 內容" value={marqueeForm.popup_content} onChange={e => setMarqueeForm(p => ({...p, popup_content: e.target.value}))} />
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      <select className="form-control form-control-sm" value={marqueeForm.scope} onChange={e => setMarqueeForm(p => ({...p, scope: e.target.value}))}><option value="GLOBAL">全站</option><option value="CHANNEL">指定頻道</option></select>
                      <select className="form-control form-control-sm" value={marqueeForm.channel_id} onChange={e => setMarqueeForm(p => ({...p, channel_id: e.target.value}))} disabled={marqueeForm.scope !== 'CHANNEL'}><option value="">選擇頻道</option>{channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}</select>
                      <select className="form-control form-control-sm" value={marqueeForm.status} onChange={e => setMarqueeForm(p => ({...p, status: e.target.value}))}><option value="ACTIVE">啟用</option><option value="DISABLED">停用</option></select>
                    </div>
                    <div style={{borderTop:'1px dashed var(--border-color)',marginTop:8,paddingTop:8}}>
                      <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:6}}>綁定快速投票（選填）：填寫後點擊跑馬燈即可當場投票{marqueeForm.poll_id && !marqueeForm.poll_question.trim() ? '（目前已綁定一個投票，留空則維持不變）' : ''}</div>
                      <input className="form-control form-control-sm" placeholder="投票題目" value={marqueeForm.poll_question} onChange={e => setMarqueeForm(p => ({...p, poll_question: e.target.value}))} />
                      <div style={{display:'flex',gap:8,margin:'6px 0'}}>
                        <select className="form-control form-control-sm" value={marqueeForm.poll_type} onChange={e => setMarqueeForm(p => ({...p, poll_type: e.target.value}))}><option value="SINGLE">單選</option><option value="MULTIPLE">複選</option></select>
                      </div>
                      <textarea className="form-control form-control-sm" rows="3" placeholder="投票選項（每行一個，至少兩個）" value={marqueeForm.poll_options} onChange={e => setMarqueeForm(p => ({...p, poll_options: e.target.value}))} />
                    </div>
                    <button className="btn btn-sm btn-primary" onClick={handleSaveMarquee}>{editingMarquee ? '更新' : '建立'}</button>
                  </div>
                </AdminModal>
              )}
              <table className="admin-table">
                <thead><tr><th>內容</th><th>期間</th><th>範圍</th><th>狀態</th><th>操作</th></tr></thead>
                <tbody>{marquees.map(m => <tr key={m.id}><td style={{color:m.color,fontStyle:m.font_style}}>{m.content}</td><td>{m.start_date || '-'} ~ {m.end_date || '-'}</td><td>{m.scope === 'CHANNEL' ? channels.find(ch => ch.id === m.channel_id)?.name || m.channel_id : '全站'}</td><td><span className={`badge badge-${m.status === 'ACTIVE' ? 'success' : 'danger'}`}>{statusLabel(m.status)}</span></td><td><div className="btn-group"><button className="btn btn-sm btn-secondary" onClick={() => { setEditingMarquee(m.id); setMarqueeForm({ content: m.content || '', start_date: m.start_date || '', end_date: m.end_date || '', color: m.color || '#007bff', font_style: m.font_style || 'normal', link_url: m.link_url || '', popup_content: m.popup_content || '', scope: m.scope || 'GLOBAL', channel_id: m.channel_id || '', status: m.status || 'ACTIVE', poll_id: m.poll_id || '', poll_question: '', poll_type: 'SINGLE', poll_options: '' }); setShowMarqueeModal(true); }}>編輯</button><button className="btn btn-sm btn-danger" onClick={() => handleDeleteMarquee(m.id)}>刪除</button></div></td></tr>)}</tbody>
              </table>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-section">
              <h2>⚙️ 系統設定</h2>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>設定項目</th>
                    <th>說明</th>
                    <th>目前值</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map((s) => (
                    <tr key={s.setting_key}>
                      <td><code>{s.setting_key}</code></td>
	                      <td>{getSettingDescription(s)}</td>
                      <td>
                        <input
                          className="form-control form-control-sm"
	                          value={settingsForm[s.setting_key] ?? ''}
	                          onChange={(e) => setSettingsForm(prev => ({ ...prev, [s.setting_key]: e.target.value }))}
                          style={{ maxWidth: 200 }}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
	                          onClick={() => handleUpdateSetting(s.setting_key, settingsForm[s.setting_key])}
                        >
                          儲存
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;

